import React, { useCallback, useMemo } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { ArrowLeft, ChevronRight } from 'lucide-react';

import Dialog from '@dashboard/components/Dialog';
import {
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@dashboard/components/ui/sidebar';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import usePage from '@dashboard/hooks/usePage';
import { useIsMobile } from '@dashboard/hooks/use-mobile';
import { NAV_ITEM_CLASS } from '@dashboard/layouts/NavigationParts';

import SectionActions, { SectionHeading } from '../SectionActions';
import { findSettingsItem, getSettingsItems } from './settingsItems';

const ITEM_HEADING_CLASS = 'text-lg font-semibold text-gray-900 dark:text-foreground';
const DESCRIPTION_CLASS = 'text-sm text-gray-500 dark:text-muted-foreground';

function ItemList({ items, current, onSelect, showChevrons }) {
  return (
    <SidebarGroup className="pt-4">
      <SidebarMenu>
        {items.map(item => {
          const Icon = item.icon;
          return (
            <SidebarMenuItem key={item.key}>
              <SidebarMenuButton
                isActive={!showChevrons && item.key === current?.key}
                className={NAV_ITEM_CLASS}
                onClick={() => onSelect(item)}
              >
                <Icon />
                <span>{item.label}</span>
                {showChevrons && (
                  <ChevronRight className="ml-auto text-muted-foreground" aria-hidden="true" />
                )}
              </SidebarMenuButton>
            </SidebarMenuItem>
          );
        })}
      </SidebarMenu>
    </SidebarGroup>
  );
}

// An item with one setting: the setting's heading would repeat the item's, so its description
// and Help / AI buttons sit in the item's header instead.
function ItemHeader({ item, lead }) {
  const only = item.sections.length === 1 ? item.sections[0] : null;

  return (
    <div className="max-w-lg">
      <div className="flex min-h-9 items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {lead}
          <h1 className={ITEM_HEADING_CLASS}>{item.label}</h1>
        </div>
        {only && <SectionActions section={only} />}
      </div>
      {only?.description && <p className={DESCRIPTION_CLASS}>{only.description}</p>}
    </div>
  );
}

function ItemSettings({ item, setting }) {
  // Scrolls a setting's own link to it once, when its item opens.
  const scrollTo = useCallback(
    node => {
      if (node && item.sections.length > 1) node.scrollIntoView({ block: 'start' });
    },
    [item],
  );

  if (item.sections.length === 1) {
    const [section] = item.sections;
    return (
      <section id={`settings-${section.path.replace(/^\//, '')}`} className="max-w-lg">
        {section.component}
      </section>
    );
  }

  return (
    <div className="divide-y divide-gray-200 dark:divide-border">
      {item.sections.map(section => {
        const path = section.path.replace(/^\//, '');
        return (
          <section
            key={section.path}
            id={`settings-${path}`}
            ref={path === setting ? scrollTo : undefined}
            className="scroll-mt-4 py-8 first:pt-0"
          >
            <SectionHeading section={section} className="max-w-lg" />
            {section.description && (
              <div className={`mb-4 max-w-lg ${DESCRIPTION_CLASS}`}>{section.description}</div>
            )}
            <div className="max-w-lg">{section.component}</div>
          </section>
        );
      })}
    </div>
  );
}

/**
 * The page's settings over the editor. `setting` is the one its URL names (`url-slug`), or none.
 * On a phone, no setting shows the list of items; picking one opens it with a way back.
 */
export default function SettingsModal({ open, setting, onSelect, onBack, onClose }) {
  const { page } = usePage();
  const { seller } = useAuth();
  const isMobile = useIsMobile();
  const items = useMemo(() => getSettingsItems({ page, seller }), [page, seller]);
  const current = findSettingsItem(items, setting);
  const showsList = isMobile && !setting;

  const select = item => onSelect(item.sections[0].path.replace(/^\//, ''));

  return (
    <Dialog
      open={open}
      onRequestClose={onClose}
      // Focusing the first item on open would show its focus ring beside the open item.
      onOpenAutoFocus={event => event.preventDefault()}
      size="5xl"
      padded={false}
      cancelButton={null}
      confirmButton={null}
      className="overflow-hidden md:h-[min(720px,90vh)] max-md:fixed max-md:inset-0 max-md:h-full max-md:max-h-none max-md:w-full max-md:max-w-none max-md:rounded-none"
    >
      {isMobile ? (
        <div className="flex h-full flex-col">
          {showsList ? (
            <>
              <DialogPrimitive.Title className="px-6 pt-6 text-lg font-bold text-gray-900 dark:text-foreground">
                Settings
              </DialogPrimitive.Title>
              <ItemList items={items} onSelect={select} showChevrons />
            </>
          ) : (
            <>
              <DialogPrimitive.Title className="sr-only">Settings</DialogPrimitive.Title>
              <div className="px-4 pb-4 pr-14 pt-4">
                <ItemHeader
                  item={current}
                  lead={
                    <button
                      type="button"
                      aria-label="Back"
                      onClick={onBack}
                      className="text-foreground/70 hover:text-foreground"
                    >
                      <ArrowLeft className="size-5" aria-hidden="true" />
                    </button>
                  }
                />
              </div>
              <div className="flex-1 overflow-y-auto px-4 pb-8">
                <ItemSettings key={current.key} item={current} setting={setting} />
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="grid h-full grid-cols-[16rem_1fr]">
          <div className="flex flex-col border-r border-gray-200 px-2 py-6 dark:border-border">
            <DialogPrimitive.Title className="px-4 text-lg font-bold text-gray-900 dark:text-foreground">
              Settings
            </DialogPrimitive.Title>
            <ItemList items={items} current={current} onSelect={select} />
          </div>
          <div className="flex min-h-0 flex-col">
            <div className="px-6 pb-4 pr-14 pt-6">
              <ItemHeader item={current} />
            </div>
            <div className="flex-1 overflow-y-auto px-6 pb-6 pt-2">
              <ItemSettings key={current.key} item={current} setting={setting} />
            </div>
          </div>
        </div>
      )}
    </Dialog>
  );
}
